/**
 * Imperative ref API for <XtsViewer3D>.
 *
 * Encapsulates everything the host can call through `viewerRef.current.*`:
 * camera + zoomToFit, getMoverWorldTransform, exportModel, exportScreenshot,
 * mover-position channel writes, and the on-demand collision query.
 *
 * Pulled out of XtsViewer3D so the top-level component stays a thin
 * `<Canvas>` shell instead of a ~700-line handle definition.
 */

import { useImperativeHandle, useRef, type RefObject } from 'react';
import {
  Box3,
  Euler,
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import {
  orientationMatrix,
  partTransformationMatrix,
  trackTransformMatrix,
} from '../../geometry/trackTransform.js';
import { buildChain, moverWorldAt } from '../../geometry/ChainBuilder.js';
import { userToChainPositionMm } from '../../geometry/positionFrame.js';
import {
  checkSamePathCollisions,
  type CheckMoverCollisionsOptions,
  type MoverCollision,
  type MoverProbe,
} from '../../geometry/moverCollision.js';
import {
  buildModuleProbes,
  checkModuleCollisions,
  type CheckModuleCollisionsOptions,
  type ModuleCollision,
  type ModuleCollisionPartInput,
  type ModuleCollisionXpuInput,
} from '../../geometry/moduleCollision.js';
import type { NormalizedXtsConfig } from '../../geometry/normalizeXtsConfig.js';
import {
  buildRuntimeState,
  computeAreaBoundingBox,
  computeBoundingBox,
  computeModuleBoundingBox,
  computeMoverBoundingBox,
  computeStationBoundingBox,
  computeTopDownOrtho,
} from './viewerHelpers.js';
import { animateCamera, resolveEasing } from './cameraAnimation.js';
import {
  captureScreenshot,
  type ScreenshotOptions,
  type ScreenshotResult,
} from './screenshot.js';
import type { MoverPositionStore } from './MoverPositionStore.js';
import type { ModuleStatusStore } from './ModuleStatusStore.js';
import { VERSION } from '../../version.js';
import type {
  CameraState,
  FocusOptions,
  FocusTarget,
  ModuleStatusEntry,
  MoverPositionEntry,
  MoverRef,
  TrackTransform,
  XtsConfig,
  XtsModelDocument,
  XtsRuntimeState,
} from '../../types.js';

export interface XtsViewer3DRef {
  zoomToFit(paddingFactor?: number): void;
  /**
   * Re-fit the orthographic top-down frustum to the current scene AABB.
   * No-op unless the live camera is orthographic (`projection='orthographic'`).
   */
  frameTopDown(paddingFactor?: number): void;
  /**
   * Animate the camera so the given target (station / area / mover / module /
   * the whole scene) fits the frame. In perspective the current view angle is
   * preserved; in orthographic top-down the frustum is re-framed and the
   * camera pans straight over the target.
   */
  focusOn(target: FocusTarget, opts?: FocusOptions): void;
  setCamera(cam: CameraState): void;
  getCamera(): CameraState | null;
  setOrientation(orientation: XtsConfig['orientation']): void;
  getMoverWorldTransform(ref: MoverRef): {
    positionMm: [number, number, number];
    rotationDegEuler: [number, number, number];
  } | null;
  getBoundingBox(): {
    min: [number, number, number];
    max: [number, number, number];
  };
  getConfig(): XtsConfig;
  getRuntimeState(): XtsRuntimeState;
  exportModel(opts?: { includeRuntime?: boolean }): XtsModelDocument;
  exportScreenshot(opts?: ScreenshotOptions): Promise<ScreenshotResult>;
  reloadAssets(): Promise<void>;
  setMoverPosition(moverId: number, partPositionMm: number): void;
  /**
   * Push mover positions in bulk. Two accepted forms:
   *
   *  1. `Record<moverId, partPositionMm>` — backwards-compatible single-
   *     part-per-mover update. The mover stays on its config-time part.
   *  2. `MoverPositionEntry[]` — array indexed by `MoverConfig.index`
   *     (entry `i` targets the mover with `index === i`); each entry
   *     supplies the `partObjectId` (= `PartConfig.objectId`) the mover is
   *     currently on plus its position. `null` / `undefined` entries are
   *     skipped — leave them untouched in the store.
   */
  setMoverPositions(
    update:
      | Record<number, number>
      | ReadonlyArray<MoverPositionEntry | null | undefined>,
  ): void;
  getMoverPosition(moverId: number): number | null;
  /**
   * Push live drive-status (warning / error) onto motor modules. The
   * `statuses` array is indexed by `ModuleEntry.globalNumber` — i.e.
   * `statuses[m.globalNumber] === { warning, error }`. `null` clears a
   * slot; `undefined` leaves it unchanged.
   */
  setModuleStatuses(
    statuses: ReadonlyArray<ModuleStatusEntry | null | undefined>,
  ): void;
  /** Clear all live module statuses (revert to config-time `ModuleEntry.status`). */
  clearModuleStatuses(): void;
  checkMoverCollisions(opts?: CheckMoverCollisionsOptions): MoverCollision[];
  /**
   * On-demand check for physically overlapping track MODULES across the
   * whole layout — different XPUs / parts placed via `trackTransform` /
   * `orientation` / `partTransformation`, plus non-adjacent modules of the
   * same part. Adjacent (seam) modules and the closed-loop seam are
   * excluded. Override-aware: reflects any `trackTransform` set imperatively
   * via the `trackTransformOverrides` prop. Returns the overlapping pairs
   * sorted deepest-overlap-first.
   */
  checkModuleCollisions(opts?: CheckModuleCollisionsOptions): ModuleCollision[];
}

export interface UseXtsViewerHandleArgs {
  ref: React.Ref<XtsViewer3DRef>;
  config: XtsConfig;
  normalized: NormalizedXtsConfig | null;
  cameraRef: RefObject<PerspectiveCamera | OrthographicCamera | null>;
  orbitRef: RefObject<OrbitControlsImpl | null>;
  glRef: RefObject<WebGLRenderer | null>;
  sceneRef: RefObject<Scene | null>;
  moverStoreRef: RefObject<MoverPositionStore>;
  moduleStatusStoreRef: RefObject<ModuleStatusStore>;
  trackTransformOverrides?: Record<number, TrackTransform>;
}

const RAD2DEG = 180 / Math.PI;

export function useXtsViewerHandle({
  ref,
  config,
  normalized,
  cameraRef,
  orbitRef,
  glRef,
  sceneRef,
  moverStoreRef,
  moduleStatusStoreRef,
  trackTransformOverrides,
}: UseXtsViewerHandleArgs): void {
  // Cancel handle for an in-flight `focusOn` tween, so a new focus call (or a
  // manual camera write) supersedes the previous animation instead of fighting it.
  const cancelFocusRef = useRef<(() => void) | null>(null);
  useImperativeHandle(
    ref,
    (): XtsViewer3DRef => {
      const isOrtho = (cam: PerspectiveCamera | OrthographicCamera): boolean =>
        (cam as OrthographicCamera).isOrthographicCamera === true;

      /** Live canvas aspect (width / height) for ortho frustum fitting. */
      const canvasAspect = (): number => {
        const c = glRef.current?.domElement;
        const w = c?.clientWidth || c?.width || 1;
        const h = c?.clientHeight || c?.height || 1;
        return h > 0 ? w / h : 1;
      };

      /** Apply a computed top-down ortho framing to an ortho camera. */
      const applyTopDown = (
        cam: OrthographicCamera,
        ctrl: OrbitControlsImpl,
        box: Box3,
        paddingFactor: number,
      ) => {
        const f = computeTopDownOrtho(box, canvasAspect(), paddingFactor);
        cam.left = f.left;
        cam.right = f.right;
        cam.top = f.top;
        cam.bottom = f.bottom;
        cam.up.set(...f.up);
        cam.position.set(...f.position);
        cam.updateProjectionMatrix();
        ctrl.target.set(...f.target);
        cam.lookAt(new Vector3(...f.target));
        ctrl.update();
      };

      /** Resolve the world AABB for a focus target. */
      const boxForTarget = (target: FocusTarget): Box3 => {
        if (!normalized) return new Box3();
        switch (target.kind) {
          case 'scene':
            return computeBoundingBox(normalized, trackTransformOverrides);
          case 'station':
            return computeStationBoundingBox(
              normalized,
              target.stationId,
              trackTransformOverrides,
            );
          case 'area':
            return computeAreaBoundingBox(
              normalized,
              target.areaId,
              trackTransformOverrides,
            );
          case 'module':
            return computeModuleBoundingBox(
              normalized,
              target.ref,
              trackTransformOverrides,
            );
          case 'mover':
            return computeMoverBoundingBox(
              normalized,
              target.ref,
              trackTransformOverrides,
              (id) => moverStoreRef.current?.get(id) ?? undefined,
            );
        }
      };

      return {
      zoomToFit: (paddingFactor = 1.2) => {
        const cam = cameraRef.current;
        const ctrl = orbitRef.current;
        if (!cam || !ctrl || !normalized) return;
        const box = computeBoundingBox(normalized, trackTransformOverrides);
        if (box.isEmpty()) return;
        if (isOrtho(cam)) {
          applyTopDown(cam as OrthographicCamera, ctrl, box, paddingFactor);
          return;
        }
        const center = box.getCenter(new Vector3());
        const size = box.getSize(new Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) * paddingFactor;
        const persp = cam as PerspectiveCamera;
        const fov = persp.fov * (Math.PI / 180);
        const dist = (maxDim / 2) / Math.tan(fov / 2);
        const dir = new Vector3(1, -1, 1).normalize();
        persp.position.copy(center).addScaledVector(dir, dist);
        persp.lookAt(center);
        ctrl.target.copy(center);
        ctrl.update();
      },
      frameTopDown: (paddingFactor = 1.1) => {
        const cam = cameraRef.current;
        const ctrl = orbitRef.current;
        if (!cam || !ctrl || !normalized || !isOrtho(cam)) return;
        const box = computeBoundingBox(normalized, trackTransformOverrides);
        if (box.isEmpty()) return;
        applyTopDown(cam as OrthographicCamera, ctrl, box, paddingFactor);
      },
      focusOn: (target, opts) => {
        const cam = cameraRef.current;
        const ctrl = orbitRef.current;
        if (!cam || !ctrl || !normalized) return;
        const box = boxForTarget(target);
        if (box.isEmpty()) return;
        // Supersede any running focus animation.
        cancelFocusRef.current?.();
        const center = box.getCenter(new Vector3());
        const size = box.getSize(new Vector3());
        const durationMs = opts?.durationMs ?? 700;
        const easing = resolveEasing(opts?.easing);

        if (isOrtho(cam)) {
          const padding = opts?.paddingFactor ?? 1.1;
          const f = computeTopDownOrtho(box, canvasAspect(), padding);
          cancelFocusRef.current = animateCamera({
            camera: cam,
            controls: ctrl,
            toPosition: new Vector3(...f.position),
            toTarget: new Vector3(...f.target),
            toFrustum: {
              left: f.left,
              right: f.right,
              top: f.top,
              bottom: f.bottom,
            },
            durationMs,
            easing,
          });
          return;
        }

        // Perspective: keep the current view direction, only dolly + re-centre.
        const persp = cam as PerspectiveCamera;
        const padding = opts?.paddingFactor ?? 1.2;
        const maxDim = Math.max(size.x, size.y, size.z) * padding;
        const fov = persp.fov * (Math.PI / 180);
        const dist = (maxDim / 2) / Math.tan(fov / 2);
        const dir = new Vector3().subVectors(persp.position, ctrl.target);
        if (dir.lengthSq() < 1e-6) dir.set(1, -1, 1);
        dir.normalize();
        const toPosition = new Vector3()
          .copy(center)
          .addScaledVector(dir, dist);
        cancelFocusRef.current = animateCamera({
          camera: persp,
          controls: ctrl,
          toPosition,
          toTarget: center,
          durationMs,
          easing,
        });
      },
      setCamera: (cam) => {
        const c = cameraRef.current;
        const ctrl = orbitRef.current;
        if (!c || !ctrl) return;
        c.position.set(...cam.positionMm);
        c.lookAt(new Vector3(...cam.targetMm));
        ctrl.target.set(...cam.targetMm);
        ctrl.update();
      },
      getCamera: () => {
        const c = cameraRef.current;
        const ctrl = orbitRef.current;
        if (!c || !ctrl) return null;
        return {
          positionMm: [c.position.x, c.position.y, c.position.z],
          targetMm: [ctrl.target.x, ctrl.target.y, ctrl.target.z],
          upAxis: 'Z',
        };
      },
      setOrientation: () => {
        // Phase-1 stub: orientation is a static prop in this build. A future
        // phase persists it via React state for live updates.
      },
      getMoverWorldTransform: (moverRef) => {
        if (!normalized) return null;
        const xpu = normalized.processingUnits.find(
          (x) => x.objectId === moverRef.processingUnitObjectId,
        );
        if (!xpu) return null;
        const mover = xpu.movers.find(
          (m) => m.index === moverRef.moverIndex,
        );
        if (!mover) return null;
        const part = xpu.parts.find((p) => p.globalNumber === mover.partOid);
        if (!part) return null;
        const userPos =
          moverStoreRef.current!.get(mover.id) ?? mover.partPositionMm;
        const chainPos = userToChainPositionMm(userPos, xpu.positionFrame);
        const pose = moverWorldAt(buildChain(part.modules), chainPos);
        if (!pose) return null;
        const tt =
          trackTransformOverrides?.[xpu.objectId] ?? xpu.trackTransform ?? null;
        const m = new Matrix4()
          .copy(orientationMatrix(config.orientation))
          .multiply(trackTransformMatrix(tt))
          .multiply(partTransformationMatrix(part.partTransformation));
        const p = new Vector3(
          pose.positionMm[0],
          pose.positionMm[1],
          pose.positionMm[2],
        ).applyMatrix4(m);
        const yawQ = new Quaternion().setFromEuler(
          new Euler(0, 0, (pose.yawDeg * Math.PI) / 180, 'XYZ'),
        );
        const localTrans = new Vector3();
        const localQ = new Quaternion();
        const localScale = new Vector3();
        m.decompose(localTrans, localQ, localScale);
        const finalQ = localQ.multiply(yawQ);
        const e = new Euler().setFromQuaternion(finalQ, 'XYZ');
        return {
          positionMm: [p.x, p.y, p.z],
          rotationDegEuler: [e.x * RAD2DEG, e.y * RAD2DEG, e.z * RAD2DEG],
        };
      },
      getBoundingBox: () => {
        const box = normalized
          ? computeBoundingBox(normalized, trackTransformOverrides)
          : new Box3();
        return {
          min: [box.min.x, box.min.y, box.min.z],
          max: [box.max.x, box.max.y, box.max.z],
        };
      },
      getConfig: () => config,
      getRuntimeState: () => buildRuntimeState(config),
      exportModel: (opts) => ({
        schemaVersion: 1,
        meta: {
          generatedAt: new Date().toISOString(),
          generator: `beckhoff-xts-viewer-3d@${VERSION}`,
        },
        config,
        ...(opts?.includeRuntime !== false
          ? { runtime: buildRuntimeState(config) }
          : {}),
      }),
      exportScreenshot: async (opts) => {
        const gl = glRef.current;
        const scene = sceneRef.current;
        if (!gl || !scene) {
          throw new Error(
            'exportScreenshot: renderer not ready (call after the component has mounted)',
          );
        }
        const orbitTarget = orbitRef.current?.target;
        const liveTargetMm: [number, number, number] | undefined = orbitTarget
          ? [orbitTarget.x, orbitTarget.y, orbitTarget.z]
          : undefined;
        const bbox = normalized
          ? computeBoundingBox(normalized, trackTransformOverrides)
          : new Box3();
        return captureScreenshot(
          {
            gl,
            scene,
            liveCamera: cameraRef.current,
            ...(liveTargetMm ? { liveTargetMm } : {}),
            bbox,
          },
          opts,
        );
      },
      setMoverPosition: (moverId, partPositionMm) => {
        moverStoreRef.current!.set(moverId, partPositionMm);
      },
      setMoverPositions: (update) => {
        const store = moverStoreRef.current!;
        if (Array.isArray(update)) {
          // Array form: entry `i` targets the mover with `index === i`.
          // Look up the mover by index across all XPUs to find its id, then
          // push `{partObjectId, partPosition}` into the store.
          if (!normalized) return;
          const moversByIndex = new Map<number, number>();
          for (const xpu of normalized.processingUnits) {
            for (const m of xpu.movers) moversByIndex.set(m.index, m.id);
          }
          for (let i = 0; i < update.length; i++) {
            const entry = update[i];
            if (entry === null || entry === undefined) continue;
            const id = moversByIndex.get(i);
            if (id === undefined) continue;
            store.setEntry(id, entry.partObjectId, entry.partPosition);
          }
        } else {
          store.setMany(update as Record<number, number>);
        }
      },
      getMoverPosition: (moverId) => {
        const v = moverStoreRef.current!.get(moverId);
        return v === undefined ? null : v;
      },
      setModuleStatuses: (statuses) => {
        moduleStatusStoreRef.current!.setMany(statuses);
      },
      clearModuleStatuses: () => {
        moduleStatusStoreRef.current!.clearAll();
      },
      checkMoverCollisions: (opts) => {
        if (!normalized) return [];
        const store = moverStoreRef.current!;
        const results: MoverCollision[] = [];
        for (const xpu of normalized.processingUnits) {
          const probes: MoverProbe[] = xpu.movers.map((m) => {
            const live = store.get(m.id);
            return {
              ref: {
                processingUnitObjectId: xpu.objectId,
                moverIndex: m.index,
              },
              id: m.id,
              partOid: m.partOid,
              moverType: xpu.moverType,
              partPositionMm: live ?? m.partPositionMm,
              ...(xpu.moverType === 'Custom' &&
              xpu.customMoverLayout?.pathLengthMm !== undefined
                ? { pathLengthMm: xpu.customMoverLayout.pathLengthMm }
                : {}),
            };
          });
          const byPart = new Map<number, MoverProbe[]>();
          for (const p of probes) {
            const arr = byPart.get(p.partOid);
            if (arr) arr.push(p);
            else byPart.set(p.partOid, [p]);
          }
          for (const [partOid, list] of byPart) {
            if (list.length < 2) continue;
            const part = xpu.parts.find((pp) => pp.globalNumber === partOid);
            if (!part) continue;
            const chain = buildChain(part.modules);
            results.push(
              ...checkSamePathCollisions(chain, partOid, list, opts),
            );
          }
        }
        // Aggregate sort so the deepest collision in the scene comes first.
        results.sort((a, b) => b.penetrationMm - a.penetrationMm);
        return results;
      },
      checkModuleCollisions: (opts) => {
        if (!normalized) return [];
        const xpus: ModuleCollisionXpuInput[] = [];
        for (const xpu of normalized.processingUnits) {
          // Override-aware effective track transform (same precedence the
          // scene uses: imperative override wins over the config value).
          const effective =
            trackTransformOverrides?.[xpu.objectId] ?? xpu.trackTransform;
          const parts: ModuleCollisionPartInput[] = [];
          for (const part of xpu.parts) {
            try {
              parts.push({
                partObjectId: part.objectId,
                partKey: part.globalNumber,
                ...(part.partTransformation
                  ? { partTransformation: part.partTransformation }
                  : {}),
                chain: buildChain(part.modules),
              });
            } catch {
              // already validated by normalizeXtsConfig — defensive.
            }
          }
          xpus.push({
            xpuObjectId: xpu.objectId,
            ...(effective ? { trackTransform: effective } : {}),
            parts,
          });
        }
        const { probes, adjacency } = buildModuleProbes(
          normalized.orientation,
          xpus,
          opts,
        );
        return checkModuleCollisions(probes, adjacency, opts);
      },
      reloadAssets: async () => {
        // Caching lives in useGLTF's global cache; full clear is not yet
        // wired in r3f without a per-instance handle. Stub for now.
      },
      };
    },
    [config, normalized, trackTransformOverrides],
  );
}
